import {
    postBillingInvoiceRerunInvoiceProcessing,
    postBillingInvoiceSaveBillingInvoiceAdjustments,
    postBillingInvoiceUpdateBillingInvoiceStatus,
    getBillingInvoiceGetBillingInvoiceById,
    postBillingInvoiceGetAdminBillingInvoiceHistoricalStatuses,
    getBillingInvoiceGetLastInvoiceUpdatedDate,
} from '@apis/Invoices';
import {
    BillingInvoice,
    BillingInvoiceAdjustment,
    BillingInvoiceAdjustmentAdjustmentOperation,
    BillingInvoiceAdjustmentPlatform,
    BillingInvoiceStatus,
    BillingInvoiceStatusStatus,
    InvoiceProcessingLogEntry,
} from '@apis/Invoices/model';
import {
    ActionIcon,
    Box,
    Button,
    Checkbox,
    Group,
    Menu,
    Modal,
    NumberInput,
    Select,
    Space,
    Stack,
    Switch,
    Text,
    Textarea,
    Title,
    Tooltip,
    useMantineTheme,
} from '@mantine/core';
import { useCallback, useEffect, useState, useMemo } from 'react';
import {
    AlertCircle,
    CaretRight,
    Check,
    ChevronDown,
    ChevronRight,
    ClipboardCheck,
    Clock,
    Copy,
    DotsVertical,
    Download,
    Eye,
    Plus,
    Upload,
} from 'tabler-icons-react';
import { useDi, useDiComponent, useDiMemo } from '@root/Services/DI';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import {
    SettingsInputRow,
    SettingsInputStack,
    SettingsLabel,
    SettingsSection,
    SettingsSectionBodyDivider,
    SettingsSectionItem,
    SettingsSectionItemBody,
    SettingsSectionItemHeader,
    SettingsSectionItemHeaderLabel,
} from '@root/Design/Settings';
import { Trash, ArrowUp, ArrowDown } from 'tabler-icons-react';
import { useModals } from '@mantine/modals';
import { ApplyToSection, BillingInvoiceAdjustmentFields } from './Components';
import { OptionMenuItems, OptionMenuItemTypes } from '@root/Components/Picker/OptionMenu';
import { EventEmitter, useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { UploadInvoiceFileForm } from './UploadInvoiceFileForm';
import { BasicApi } from '@root/Services/BasicApi';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';
import { SidePanelToolbarEl } from '@root/Design/SidePanel';
import { useNav } from '@root/Services/NavigationService';
import { Route } from '@root/Services/Router/RouteSerializer';
import { AnchorButton, TooltipWhite } from '@root/Design/Primitives';
import { DatePicker } from '@mantine/dates';
import { naiveJsonCopy } from '@root/Services/QueryExpr';
import { LineItemImporter } from './LineItemImporter';
import { CopyAdjustmentModal } from './CopyAdjustmentModal';
import { CopyAllAdjustmentsModal } from './CopyAllAdjustmentsModal';
import { Logger } from '@root/Services/Logger';
import { inject, injectable } from 'tsyringe';
import { FillerSwitch } from '@root/Design/Filler';
import { Company } from '@apis/Customers/model';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { ResellCostComparison } from './ResellCostComparison';
import { OverrideUpdatesEditor } from './OverrideUpdatesEditor';

const adjustmentOperationLabels: Record<BillingInvoiceAdjustmentAdjustmentOperation, string> = {
    CustomLineItems: 'Add custom line items, imported from spreadsheet',
    ExistingAmount: 'Adjust line item costs by a specific amount',
    ExistingPercent: 'Adjust line item costs by a percent %',
    NewAmount: 'Add line items with a specific $ amount',
    NewPercentOfTotal: 'Add line items based on % of total invoice amount',
    NewPercentOfLineItems: "Add line items based on % of filtered line items' total",
    OmitLineItems: 'Remove line items based on filter criteria',
    OverrideItems: 'Override data of existing line items',
};

const adjustmentOptions = Object.values(BillingInvoiceAdjustmentAdjustmentOperation).map((operation) => ({
    value: operation,
    label: adjustmentOperationLabels[operation],
}));

const platformOptions = Object.values(BillingInvoiceAdjustmentPlatform).map((platform) => ({
    value: platform,
    label: platform,
}));

@injectable()
class InvoiceDetailsModel {
    private _originalAdjustments: BillingInvoiceAdjustment[] = [];

    public readonly initializing = new EventEmitter<boolean>(true);
    public readonly savingAdjustments = new EventEmitter<boolean>(false);
    public readonly adjustmentsInvalidated = EventEmitter.empty();

    public invoice!: BillingInvoice;
    public adjustments: BillingInvoiceAdjustment[] = [];

    public constructor(
        @inject(Logger) private readonly logger: Logger,
        @inject(CompanyContextService) private readonly companyCtx: CompanyContextService<Company>,
        @inject(NotificationService) private readonly notificationSvc: NotificationService
    ) {}
    public init(invoiceId: number) {
        this.loadInvoice(invoiceId);
        return this;
    }

    public saveAdjustments = async () => {
        let result = false;
        try {
            this.savingAdjustments.emit(true);
            if (this.hasAdjustmentChanges()) {
                await postBillingInvoiceSaveBillingInvoiceAdjustments(
                    this.adjustments,
                    { billingInvoiceId: this.invoice.Id },
                    this.companyCtx.getRequestOptions(true)
                );
                result = true;
                this._originalAdjustments = naiveJsonCopy(this.adjustments);
                this.adjustmentsInvalidated.emit();
                this.notificationSvc.notify('Adjustments Saved', '', 'success', <Check />);
            }
        } catch (error) {
            if (error && error instanceof Error) {
                error.message = JSON.stringify({ originalMessage: error.message, adjustments: this.adjustments });
            }
            this.logger.error('Error saving billing adjustments', { error });
            this.notificationSvc.notify('Failed! Do NOT refresh or close the browser!', 'Contact dev support.', 'error', <AlertCircle />);
        } finally {
            this.savingAdjustments.emit(false);
        }
        return result;
    };

    public saveStatus = async (comments: string, status?: BillingInvoiceStatusStatus) => {
        let result = false;
        try {
            if (comments || (status && status !== this.invoice?.Status)) {
                const invoiceStatus: BillingInvoiceStatus = {
                    Comments: comments,
                    Status: status,
                    BillingInvoiceId: this.invoice?.Id,
                    Snapshot: this.invoice?.FinalizedSnapshot,
                };
                await postBillingInvoiceUpdateBillingInvoiceStatus(invoiceStatus, this.companyCtx.getRequestOptions(true));
                result = true;
                this.notificationSvc.notify('Status Updated', '', 'success', <Check />);
            }
        } catch (error) {
            this.logger.error('Error updating billing invoice status', { error });
            this.notificationSvc.notify('Failed to Update Status', 'Contact dev support.', 'error', <AlertCircle />);
        }
        return result;
    };

    public applyAdjustmentChanges(handler: (currAdjustments: BillingInvoiceAdjustment[]) => BillingInvoiceAdjustment[]): void;
    public applyAdjustmentChanges(adjustments: BillingInvoiceAdjustment[]): void;
    public applyAdjustmentChanges(arg: BillingInvoiceAdjustment[] | ((currAdjustments: BillingInvoiceAdjustment[]) => BillingInvoiceAdjustment[])) {
        this.adjustments = typeof arg === 'function' ? arg(this.adjustments) : arg;
        this.adjustmentsInvalidated.emit();
    }

    public hasAdjustmentChanges() {
        const prevAdjustments = JSON.stringify(this._originalAdjustments);
        const nextAdjustments = JSON.stringify(this.adjustments);
        return prevAdjustments !== nextAdjustments;
    }

    private async loadInvoice(invoiceId: number) {
        this.initializing.emit(true);
        try {
            const invoice = await getBillingInvoiceGetBillingInvoiceById({ invoiceId }, this.companyCtx.getRequestOptions(true));
            const adjustments = (invoice.Adjustments ?? []).sort((a, b) => a.Order! - b.Order!);
            if (invoice) {
                this._originalAdjustments = naiveJsonCopy(adjustments);
                this.adjustments = adjustments;
            }
            this.invoice = invoice;
        } catch (error) {
            this.logger.error(`Error loading billing invoice, id ${invoiceId}`, { error });
        } finally {
            this.initializing.emit(false);
        }
    }
}

export function InvoiceDetailsForm(props: {
    onClose: (didEdit: boolean) => void;
    onCopy: () => void;
    onSave: () => void;
    billingInvoice: BillingInvoice;
    onReprocess?: () => void;
}) {
    const model = useDiMemo(InvoiceDetailsModel, [props.billingInvoice.Id], (m) => m.init(props.billingInvoice.Id!));
    const initializing = useEventValue(model.initializing);
    return (
        <FillerSwitch loading={initializing} noData={!model.invoice} noDataMessage="Failed to load invoice details">
            {() => (
                <InvoiceDetailsFormContent
                    onClose={props.onClose}
                    onCopy={props.onCopy}
                    onSave={props.onSave}
                    onReprocess={props.onReprocess}
                    model={model}
                />
            )}
        </FillerSwitch>
    );
}

function InvoiceDetailsFormContent(props: {
    onClose: (didEdit: boolean) => void;
    onCopy: () => void;
    onSave: () => void;
    onReprocess?: () => void;
    model: InvoiceDetailsModel;
}) {
    const { model, onClose, onCopy, onSave } = props;
    const modals = useModals();
    const DiContainer = useDiComponent();
    useEvent(model.adjustmentsInvalidated);

    const handleCopyAllTo = useCallback(() => {
        if (model.adjustments.length === 0) {
            return;
        }
        const adjustmentsCopy = model.adjustments;
        const onCloseCopyAll = (adjustmentsCopied: boolean) => {
            modals.closeModal(id);
            if (adjustmentsCopied) {
                onCopy();
            }
        };
        const id = modals.openModal({
            title: 'Copy All Invoice Adjustments',
            children: (
                <DiContainer>
                    <CopyAllAdjustmentsModal
                        onClose={onCloseCopyAll}
                        companyId={model.invoice.CompanyId!}
                        currentInvoiceId={model.invoice.Id!}
                        adjustments={adjustmentsCopy}
                    />
                </DiContainer>
            ),
        });
    }, [model, modals, DiContainer, onCopy]);

    const addAdjustment = useCallback(
        (adjustment: BillingInvoiceAdjustment) => {
            if (adjustment) {
                const existingAdjustment = model.adjustments.find((existing) => existing.SourceKey === adjustment.SourceKey);

                if (!existingAdjustment) {
                    adjustment.Order = (model.adjustments?.length ?? 0) + 1;
                    model.applyAdjustmentChanges((curr) => [...curr, adjustment]);
                } else if (adjustment.SourceKey === 'PriorPeriodAdjustments:All' && adjustment.AdjustmentOperation === 'CustomLineItems') {
                    const existingImportFields = existingAdjustment.ImportFields ? JSON.parse(existingAdjustment.ImportFields) : [];
                    const newImportFields = adjustment.ImportFields ? JSON.parse(adjustment.ImportFields) : [];
                    const mergedImportFields = [...existingImportFields, ...newImportFields];

                    model.applyAdjustmentChanges((curr) =>
                        curr.map((adj) =>
                            adj.SourceKey === adjustment.SourceKey ? { ...adj, ImportFields: JSON.stringify(mergedImportFields) } : adj
                        )
                    );
                }
            }
        },
        [model]
    );

    return (
        <Stack sx={{ height: '100%' }} spacing={0}>
            <Box px="lg" sx={{ flex: 1, overflow: 'auto', height: '100%' }}>
                <Box py="md">
                    <InvoiceDetailsToolbar
                        invoice={model.invoice}
                        onAdjustmentAdded={addAdjustment}
                        onCopyAllAdjustments={handleCopyAllTo}
                        onReprocess={props.onReprocess}
                    />
                </Box>
                <InvoiceDetailsStatusSection onSave={model.saveStatus} invoice={model.invoice} onClose={onClose} />
                <Space h="md" />
                <InvoiceAdjustmentsSection model={model} onCopy={onCopy} />
            </Box>
            {/* Save */}
            <SidePanelToolbarEl>
                <InvoiceDetailsPanelControls model={model} onClose={onClose} onSave={onSave} />
            </SidePanelToolbarEl>
        </Stack>
    );
}

function InvoiceDetailsPanelControls(props: { model: InvoiceDetailsModel; onClose: (didEdit: boolean) => void; onSave: () => void }) {
    const { model, onClose, onSave } = props;
    useEvent(model.adjustmentsInvalidated);
    const isSaving = useEventValue(model.savingAdjustments);
    const canSave = !isSaving && model.hasAdjustmentChanges();
    const handleSave = useCallback(
        async (close: boolean) => {
            if (await model.saveAdjustments()) {
                onSave();
                if (close) {
                    onClose(true);
                }
            }
        },
        [model, onClose, onSave]
    );
    return (
        <>
            <Button onClick={() => props.onClose(false)} variant="outline">
                Cancel
            </Button>
            <Button onClick={() => handleSave(false)} disabled={!canSave}>
                Save
            </Button>
            <Button onClick={() => handleSave(true)} disabled={!canSave}>
                Save and Close
            </Button>
        </>
    );
}

function InvoiceDetailsToolbar(props: {
    invoice: BillingInvoice;
    onAdjustmentAdded: (adjustment: BillingInvoiceAdjustment) => void;
    onCopyAllAdjustments: () => void;
    onReprocess?: () => void;
}) {
    const { invoice, onAdjustmentAdded, onCopyAllAdjustments } = props;
    const fmtSvc = useDi(FormatService);
    const basicApi = useDi(BasicApi);
    const companyCtx = useDi(CompanyContextService);

    const [canRun, setCanRun] = useState(true);
    const [uploadModalOpened, { open: openFileUploadModal, close: closeFileUploadModal }] = useToggle(false);
    const [costComparisonModalOpened, { open: openCostComparisonModal, close: closeCostComparisonModal }] = useToggle(false);
    const [downloadReady, setDownloadReady] = useState(invoice.FileKey ? true : false);

    const DiContainer = useDiComponent();

    const downloadFile = useCallback(() => {
        const params = {
            invoiceId: invoice.Id,
        };

        const fileName = `Cloud Invoice - ${invoice.Year}-${invoice.Month!.toString().padStart(2, '0')}`;
        basicApi.download(`${fileName}.pdf`, { url: '/BillingInvoice/DownloadInvoiceFile', method: 'GET', params }, 'Invoices');
    }, [invoice.Id]);

    const { getRootUrl } = useNav();
    const invoiceExplorerUrl = useMemo(() => {
        const id = invoice.CompanyId ?? 0;
        const month = `${invoice.Year}${invoice.Month!.toString().padStart(2, '0')}01`;
        const route = [
            { name: 'manage-company', data: { id } },
            { name: 'FinOps', data: {} },
            { name: 'invoice-explorer', data: { month } },
        ] as Route;
        return getRootUrl(route);
    }, []);

    const reRunInvoice = useCallback(async () => {
        companyCtx.withParentCompany(() => {
            setCanRun(false);
            postBillingInvoiceRerunInvoiceProcessing(invoice).finally(() => {
                setTimeout(() => {
                    setCanRun(true);
                }, 10000);
            });
        });
        props.onReprocess?.();
    }, [invoice, props.onReprocess]);

    const closeFileUpload = useCallback((uploaded?: boolean) => {
        if (uploaded) {
            setDownloadReady(true);
        }
        closeFileUploadModal();
    }, []);
    return (
        <>
            <Stack spacing="xs">
                <Title order={4}>{`Invoice Amount: ${fmtSvc.formatMoney(invoice.Total!)}`}</Title>

                <Group>
                    <Tooltip label={'Compare Invoice with Resale costs'} withArrow>
                        <Button variant="outline" onClick={openCostComparisonModal}>
                            <ClipboardCheck />
                        </Button>
                    </Tooltip>
                    <Tooltip label={downloadReady ? 'Download invoice file' : 'No invoice file has been uploaded'} withArrow>
                        <Button variant="outline" onClick={downloadFile} disabled={!downloadReady}>
                            <Download />
                        </Button>
                    </Tooltip>
                    <Tooltip label={invoice.Status !== 'Finalized' ? 'Upload Invoice' : "Can't Upload File to finalized invoice"} withArrow>
                        <Button variant="outline" onClick={openFileUploadModal} disabled={invoice.Status === 'Finalized'}>
                            <Upload />
                        </Button>
                    </Tooltip>
                    <Tooltip label={'View in Invoice Explorer'} withArrow>
                        <Button component="a" target="_blank" variant="outline" href={invoiceExplorerUrl}>
                            <Eye />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Reprocess Invoice" withArrow>
                        <Button onClick={reRunInvoice} variant={canRun ? 'outline' : 'default'} disabled={!canRun || invoice.Status !== 'Draft'}>
                            <CaretRight />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Copy all adjustments to another month" withArrow>
                        <Button variant="outline" onClick={onCopyAllAdjustments}>
                            <Copy />
                        </Button>
                    </Tooltip>
                </Group>
            </Stack>

            <Modal opened={uploadModalOpened} closeOnClickOutside onClose={closeFileUpload} title="Upload Invoice File">
                <DiContainer>
                    <UploadInvoiceFileForm invoiceId={invoice.Id!} onClose={closeFileUpload}></UploadInvoiceFileForm>
                </DiContainer>
            </Modal>

            <Modal
                opened={costComparisonModalOpened}
                onClose={closeCostComparisonModal}
                size={'calc(100vw - 200px)'}
                centered
                radius="md"
                withCloseButton={false}
            >
                <DiContainer>
                    {costComparisonModalOpened && (
                        <ResellCostComparison
                            initialMonth={new Date(invoice.Year!, invoice.Month! - 1)}
                            invoiceId={invoice.Id!}
                            onClose={closeCostComparisonModal}
                            onAdjustmentAdded={props.onAdjustmentAdded}
                        />
                    )}
                </DiContainer>
            </Modal>
        </>
    );
}

interface InvoiceDetailsStatusSectionProps {
    invoice: BillingInvoice;
    onSave: (comment: string, status?: BillingInvoiceStatusStatus) => Promise<boolean>;
    onClose: (didEdit: boolean) => void;
}
function InvoiceDetailsStatusSection({ invoice, onSave, onClose }: InvoiceDetailsStatusSectionProps) {
    const fmtSvc = useFmtSvc();
    const companyCtx = useDi(CompanyContextService);
    const [status, setStatus] = useState(invoice.Status);
    const [comments, setComments] = useState('');
    const statusOptions = ['Draft', 'Finalized', 'Sent', 'Paid', 'Voided', 'Deleted'].map((o) => ({ value: o, label: o }));

    const [statusHistory, setStatusHistory] = useState<BillingInvoiceStatus[]>([]);
    const [historyExpanded, { toggle: toggleHistory }] = useToggle(false);

    useEffect(() => {
        (async () => {
            const statusHistory = await postBillingInvoiceGetAdminBillingInvoiceHistoricalStatuses([invoice.Id!], companyCtx.getRequestOptions(true));
            if (statusHistory) {
                setStatusHistory(statusHistory.sort((a, b) => new Date(b.ModifiedAt!).getTime() - new Date(a.ModifiedAt!).getTime()));
            }
        })();
    }, [invoice.Id]);

    const canSave = status !== invoice.Status || comments !== '';

    return (
        <SettingsSection title={`Status: ${status}`} collapsed={true}>
            <SettingsSectionItem>
                <SettingsSectionItemHeader>
                    <SettingsSectionItemHeaderLabel>Invoice Status</SettingsSectionItemHeaderLabel>
                </SettingsSectionItemHeader>
                <SettingsSectionItemBody>
                    <SettingsInputRow>
                        <SettingsLabel>Update Status</SettingsLabel>
                        <Select data={statusOptions} value={status} onChange={(v) => setStatus(v as BillingInvoice['Status'])}></Select>
                    </SettingsInputRow>
                    <SettingsInputStack>
                        <SettingsLabel>Comments</SettingsLabel>
                        <Textarea value={comments} onChange={(e) => setComments(e.target.value)}></Textarea>
                    </SettingsInputStack>
                    <SettingsInputRow>
                        <Button
                            disabled={!canSave}
                            onClick={async () => {
                                if (await onSave(comments, status)) onClose(true);
                            }}
                        >
                            Update Status
                        </Button>
                    </SettingsInputRow>
                    {/* Status History */}
                    {statusHistory && statusHistory.length > 0 && (
                        <SettingsSectionItemHeaderLabel>
                            <Button variant="subtle" onClick={toggleHistory}>
                                {historyExpanded ? 'Hide History' : 'Show History'}
                            </Button>
                            {!historyExpanded ? null : (
                                <SettingsSection title="Status History">
                                    {statusHistory.map((status, index) => (
                                        <SettingsSectionItem key={index}>
                                            <SettingsSectionItemHeader>
                                                <SettingsSectionItemHeaderLabel>{status.Status}</SettingsSectionItemHeaderLabel>
                                            </SettingsSectionItemHeader>
                                            <SettingsSectionItemBody>
                                                <Text>Date Changed: {fmtSvc.toShortDate(fmtSvc.toLocalDate(status.ModifiedAt!))}</Text>
                                                <Text>Comments: {status.Comments}</Text>
                                            </SettingsSectionItemBody>
                                        </SettingsSectionItem>
                                    ))}
                                </SettingsSection>
                            )}
                        </SettingsSectionItemHeaderLabel>
                    )}
                </SettingsSectionItemBody>
            </SettingsSectionItem>
        </SettingsSection>
    );
}

function InvoiceAdjustmentsSection({ model, onCopy }: { model: InvoiceDetailsModel; onCopy: () => void }) {
    const modals = useModals();
    const DiContainer = useDiComponent();
    useEvent(model.adjustmentsInvalidated);

    const adjustments = model.adjustments;
    const setAdjustments = useMemo(() => model.applyAdjustmentChanges.bind(model), [model]);

    const handleAddAdjustment = () => {
        const nextOrder = adjustments.length === 0 ? 1 : Math.max(...adjustments.map((a) => a.Order ?? 0)) + 1;

        setAdjustments([
            ...adjustments,
            {
                Description: '',
                BillingInvoiceId: model.invoice.Id,
                Amount: 0,
                Fields: [],
                AdjustmentOperation: undefined,
                Order: nextOrder,
            },
        ]);
    };

    const handleAdjustmentChange = (index: number, updater: (adjustment: BillingInvoiceAdjustment) => BillingInvoiceAdjustment) => {
        setAdjustments((prevAdjustments) => {
            const updatedAdjustments = prevAdjustments.map((adj, i) => (i === index ? updater(adj) : adj));
            return updatedAdjustments;
        });
    };

    const handleDeleteAdjustment = (index: number) => {
        setAdjustments((prevAdjustments) => {
            const updated = [...prevAdjustments];
            updated.splice(index, 1);
            return reorderAdjustments(updated);
        });
    };

    const handleCopyAdjustment = (index: number) => {
        setAdjustments((prevAdjustments) => {
            const adjustment = naiveJsonCopy(prevAdjustments[index]);
            adjustment.Id = undefined;
            adjustment.SourceKey = undefined;
            const updated = [...prevAdjustments, adjustment];
            return reorderAdjustments(updated);
        });
    };

    const handleCopyAdjustmentTo = (index: number) => {
        const adjustment = naiveJsonCopy(adjustments[index]);
        const onCloseCopy = (adjustmentCopied: boolean) => {
            modals.closeModal(id);
            if (adjustmentCopied) {
                onCopy();
            }
        };

        const id = modals.openModal({
            title: 'Copy Invoice Adjustment',
            children: (
                <DiContainer>
                    <CopyAdjustmentModal onClose={onCloseCopy} companyId={model.invoice.CompanyId!} adjustment={adjustment}></CopyAdjustmentModal>
                </DiContainer>
            ),
        });
    };

    const reorderAdjustments = (list: BillingInvoiceAdjustment[]) => {
        return list.map((adj, idx) => ({ ...adj, Order: idx + 1 }));
    };

    const moveItem = (from: number, to: number) => {
        setAdjustments((prevAdjustments) => {
            const updated = [...prevAdjustments];
            const [moved] = updated.splice(from, 1);
            updated.splice(to, 0, moved);
            return reorderAdjustments(updated);
        });
    };

    const handleMoveUp = (index: number) => {
        if (index > 0) moveItem(index, index - 1);
    };

    const handleMoveDown = (index: number) => {
        if (index < adjustments.length - 1) moveItem(index, index + 1);
    };

    const baseAdjProps = {
        billingInvoice: model.invoice,
        listLength: adjustments.length,
        onCopy: handleCopyAdjustment,
        onChange: handleAdjustmentChange,
        onDelete: handleDeleteAdjustment,
        onMoveUp: handleMoveUp,
        onMoveDown: handleMoveDown,
        onCopyTo: handleCopyAdjustmentTo,
    };

    return (
        <SettingsSection title={'Adjustments'} collapsed={false}>
            {adjustments.map((adjustment, index) => (
                <InvoiceAdjustment key={index} {...{ ...baseAdjProps, adjustment, index, listLength: adjustments.length }} />
            ))}
            <SettingsSectionItem style={{ flex: 1 }} anchor onClick={handleAddAdjustment}>
                <AnchorButton size="sm" icon={<Plus size={16} />} text={`Add ${adjustments.length > 0 ? 'another' : 'an'} adjustment`} />
            </SettingsSectionItem>
        </SettingsSection>
    );
}

interface InvoiceAdjustmentProps {
    adjustment: BillingInvoiceAdjustment;
    billingInvoice: BillingInvoice;
    index: number;
    listLength: number;
    onCopy: (index: number) => void;
    onChange: (index: number, updater: (adjustment: BillingInvoiceAdjustment) => BillingInvoiceAdjustment) => void;
    onDelete: (index: number) => void;
    onMoveUp: (index: number) => void;
    onMoveDown: (index: number) => void;
    onCopyTo: (index: number) => void;
}
function InvoiceAdjustment(props: InvoiceAdjustmentProps) {
    const fmtSvc = useFmtSvc();
    const { adjustment, billingInvoice, index, listLength, onCopy, onChange, onDelete, onMoveUp, onMoveDown, onCopyTo } = props;
    const [expanded, { toggle }] = useToggle(!adjustment.Id);
    const isExactAmount = (adjustment: BillingInvoiceAdjustment) =>
        adjustment.AdjustmentOperation === 'NewAmount' || adjustment.AdjustmentOperation === 'ExistingAmount';
    const handleCopy = useCallback(() => onCopy(index), [index, onCopy]);
    const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);
    const handleOnMoveUp = useCallback(() => onMoveUp(index), [index, onMoveUp]);
    const handleOnMoveDown = useCallback(() => onMoveDown(index), [index, onMoveDown]);
    const handleCopyTo = useCallback(() => onCopyTo(index), [index, onCopyTo]);
    const handleAdjustmentChange = useCallback(
        <T extends keyof BillingInvoiceAdjustment>(field: T, value: BillingInvoiceAdjustment[T]) => {
            onChange(index, (adjustment: BillingInvoiceAdjustment) => {
                const updatedAdjustment = { ...adjustment, [field]: value };

                if (!isExactAmount(updatedAdjustment)) {
                    updatedAdjustment.Platform = undefined;
                } else if (updatedAdjustment.AdjustmentOperation !== 'CustomLineItems') {
                    updatedAdjustment.ImportFields = undefined;
                }
                if (updatedAdjustment.AdjustmentOperation === 'OverrideItems') {
                    updatedAdjustment.Amount = 0;
                    updatedAdjustment.Platform = undefined;
                    updatedAdjustment.ImportFields = undefined;
                    updatedAdjustment.Fields = [];
                }
                if (updatedAdjustment.AdjustmentOperation !== 'OverrideItems') {
                    updatedAdjustment.OverrideUpdates = undefined;
                }
                return updatedAdjustment;
            });
        },
        [index, onChange]
    );
    const options = useMemo(
        () =>
            [
                { label: 'Copy Adjustment', onClick: handleCopy, icon: <Copy size={16} /> },
                ...(index > 0 ? [{ label: 'Move Up', onClick: handleOnMoveUp, icon: <ArrowUp size={16} /> }] : []),
                ...(index < listLength - 1 ? [{ label: 'Move Down', onClick: handleOnMoveDown, icon: <ArrowDown size={16} /> }] : []),
                { label: 'Delete Adjustment', onClick: handleDelete, icon: <Trash size={16} /> },
                { label: 'Copy to Month...', onClick: handleCopyTo, icon: <Copy size={16} /> },
            ] as OptionMenuItemTypes[],
        [index, listLength, handleCopy, handleDelete, handleOnMoveUp, handleOnMoveDown, handleCopyTo]
    );
    const amtType = isExactAmount(adjustment) ? '$' : '%';
    const adjOp = adjustment.AdjustmentOperation ?? 'NewAmount';

    const showPlatformPicker = ['NewAmount', 'ExistingAmount'].includes(adjOp);
    const showAdjustmentDates = ['NewAmount', 'NewPercentOfLineItems', 'NewPercentOfTotal'].includes(adjOp);
    const showAmount = ['NewAmount', 'NewPercentOfTotal', 'NewPercentOfLineItems', 'ExistingAmount', 'ExistingPercent'].includes(adjOp);
    const showApplyTo = [
        'NewPercentOfTotal',
        'NewPercentOfLineItems',
        'ExistingAmount',
        'ExistingPercent',
        'OmitLineItems',
        'OverrideItems',
    ].includes(adjOp);
    const showFieldInput = ['NewAmount', 'NewPercentOfTotal', 'NewPercentOfLineItems'].includes(adjOp);
    const showImport = ['CustomLineItems'].includes(adjOp);
    const showOverrideUpdates = adjOp === 'OverrideItems';
    const isActive = adjustment.Active !== false;

    return (
        <SettingsSectionItem>
            <SettingsSectionItemHeader>
                <Group spacing={4}>
                    <ActionIcon onClick={toggle}>{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</ActionIcon>

                    <SettingsSectionItemHeaderLabel>Adjustment {index + 1}</SettingsSectionItemHeaderLabel>
                </Group>
                <Group spacing={8} noWrap>
                    <Tooltip label={isActive ? 'Deactivate adjustment' : 'Activate adjustment'} withArrow>
                        <Switch checked={isActive} onChange={(e) => handleAdjustmentChange('Active', e.currentTarget.checked)} size="sm" />
                    </Tooltip>
                    <Menu withinPortal position="bottom-end" shadow="md">
                        <Menu.Target>
                            <ActionIcon variant="subtle">
                                <DotsVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <OptionMenuItems options={options} close={() => {}} />
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </SettingsSectionItemHeader>
            <SettingsSectionItemBody>
                <SettingsInputStack>
                    <Group noWrap>
                        <Select
                            data={adjustmentOptions}
                            value={adjustment.AdjustmentOperation}
                            onChange={(value) => handleAdjustmentChange('AdjustmentOperation', value as BillingInvoiceAdjustmentAdjustmentOperation)}
                            placeholder="Select Adjustment Type"
                            sx={{ flex: 1 }}
                        ></Select>
                    </Group>
                </SettingsInputStack>
                <SettingsInputStack>
                    <SettingsLabel>Description</SettingsLabel>
                    <Textarea value={adjustment.Description!} onChange={(e) => handleAdjustmentChange('Description', e.target.value)}></Textarea>
                </SettingsInputStack>
            </SettingsSectionItemBody>
            {!expanded ? null : (
                <>
                    <SettingsSectionBodyDivider />
                    {!adjustment.AdjustmentOperation ? null : (
                        <>
                            <SettingsSectionItemBody>
                                {showPlatformPicker ? (
                                    <SettingsInputRow>
                                        <SettingsLabel>Cloud Platform</SettingsLabel>
                                        <Select
                                            data={platformOptions}
                                            value={adjustment.Platform}
                                            onChange={(value) => handleAdjustmentChange('Platform', value as BillingInvoiceAdjustmentPlatform)}
                                            style={{ width: '250px' }}
                                        ></Select>
                                    </SettingsInputRow>
                                ) : null}
                                {!showAmount ? null : (
                                    <SettingsInputRow>
                                        <SettingsLabel>Amount ({amtType})</SettingsLabel>
                                        <NumberInput
                                            value={adjustment.Amount!}
                                            precision={5}
                                            onChange={(value) => handleAdjustmentChange('Amount', value || 0)}
                                        ></NumberInput>
                                    </SettingsInputRow>
                                )}
                                <Box>
                                    {!showAdjustmentDates ? null : (
                                        <>
                                            <SettingsInputRow>
                                                <SettingsLabel>Adjustment Start Date</SettingsLabel>
                                                <DatePicker
                                                    value={adjustment.StartDate ? new Date(adjustment.StartDate) : null}
                                                    onChange={(date) => handleAdjustmentChange('StartDate', date as unknown as string)}
                                                />
                                            </SettingsInputRow>
                                            <SettingsInputRow>
                                                <SettingsLabel>Adjustment End Date</SettingsLabel>
                                                <DatePicker
                                                    value={adjustment.EndDate ? new Date(adjustment.EndDate) : null}
                                                    onChange={(date) => handleAdjustmentChange('EndDate', date as unknown as string)}
                                                />
                                            </SettingsInputRow>
                                        </>
                                    )}
                                </Box>
                            </SettingsSectionItemBody>
                            {!showApplyTo ? null : (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody style={{ margin: 'calc(-1 * var(--settings-body-padding))' }}>
                                        <ApplyToSection
                                            adjustment={adjustment}
                                            billingInvoice={billingInvoice}
                                            showCollapseHeader={true}
                                            onChanges={(updated) => handleAdjustmentChange('ApplyTo', updated.ApplyTo)}
                                        />
                                    </SettingsSectionItemBody>
                                </>
                            )}
                            {!showFieldInput ? null : (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody style={{ margin: 'calc(-1 * var(--settings-body-padding))' }}>
                                        <BillingInvoiceAdjustmentFields
                                            invoice={billingInvoice}
                                            fields={adjustment.Fields!}
                                            onChange={(fields) => handleAdjustmentChange('Fields', fields || [])}
                                        />
                                    </SettingsSectionItemBody>
                                </>
                            )}
                            {!showImport ? null : (
                                <SettingsSectionItemBody style={{ marginTop: 'calc(-2 * var(--settings-body-padding))' }}>
                                    <LineItemImporter
                                        invoice={billingInvoice}
                                        onChange={(v) => handleAdjustmentChange('ImportFields', v)}
                                        value={adjustment.ImportFields ?? null}
                                    />
                                </SettingsSectionItemBody>
                            )}
                            {showOverrideUpdates && (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody
                                        style={{
                                            margin: 'calc(-1 * var(--settings-body-padding))',
                                        }}
                                    >
                                        <OverrideUpdatesEditor
                                            invoice={billingInvoice}
                                            value={adjustment.OverrideUpdates ?? null}
                                            onChange={(v) => handleAdjustmentChange('OverrideUpdates', v)}
                                        />
                                    </SettingsSectionItemBody>
                                </>
                            )}
                            <SettingsSectionBodyDivider />
                            <SettingsSectionItemBody>
                                <SettingsInputStack>
                                    <TooltipWhite label="If checked, given that the next billing period's invoice does not exist yet, when the next billing period's invoice is first created, this adjustment will be automatically copied to the new invoice.">
                                        <Checkbox
                                            checked={adjustment.CopyToNextMonth}
                                            onChange={(e) => handleAdjustmentChange('CopyToNextMonth', e.target.checked ? true : false)}
                                            label="Auto-copy this adjustment to next month's invoice"
                                        />
                                    </TooltipWhite>
                                </SettingsInputStack>
                            </SettingsSectionItemBody>
                            {false && (
                                <>
                                    <SettingsSectionBodyDivider />
                                    <SettingsSectionItemBody>
                                        <SettingsInputRow>
                                            <SettingsLabel>Adjustment Amount</SettingsLabel>
                                            <SettingsLabel>{fmtSvc.formatMoney(0)}</SettingsLabel>
                                        </SettingsInputRow>
                                    </SettingsSectionItemBody>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </SettingsSectionItem>
    );
}
